import * as ngCore from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import * as ngRouter from '@angular/router'
import * as ents from '@attain-apps/entities'
import { environment } from '@attain-apps/fe/web/main/env'
import { TypeChecker } from '@attain-apps/type-guards'
import { ComponentStore } from '@ngrx/component-store'
import * as rxjs from 'rxjs'

import * as state from '../../../state'

import { Config } from './config'
import { State } from './state'

// NOTE: must match CSS
const CONFIG_TRANSITION_TIME = 1.2e3

const NAVBAR_HOVER_EXPAND_DELAY = 300

// NOTE: must match CSS
const NAVBAR_COLLAPSED_WIDTH = 50

const PINNED_LOCAL_STORAGE_KEY = 'attain-apps.navbar.pinned'
const EXPANDED_TENANT_IDS_LOCAL_STORAGE_KEY =
  'attain-apps.navbar.expandedTenantIds'
const EXPANDED_APP_IDS_LOCAL_STORAGE_KEY = 'attain-apps.navbar.expandedAppIds'

/**
 * Manages the {@link State} of the navbar
 */
@ngCore.Injectable({ providedIn: 'root' })
export class Store extends ComponentStore<State> {
  /** @see {@link state.me.Feature} */
  private readonly _meFeature = ngCore.inject(state.me.Feature)

  /** @see {@link ngRouter.Router} */
  private readonly _router = ngCore.inject(ngRouter.Router)

  /**
   * Represents the current URL in the browser
   */
  private readonly _currentUrlSg = toSignal(
    this._router.events.pipe(
      rxjs.filter(event => event instanceof ngRouter.NavigationEnd),
      rxjs.map(() => this._router.url),
      rxjs.startWith(this._router.url),
    ),
  )

  /**
   * Tenants that are available to be chosen
   */
  public readonly availableTenantsSg =
    this._meFeature.contexts.selectors.availableTenants.asSignal

  /** @see {@link State.expandedTenantIds} */
  public readonly expandedTenantIdsSg = this.selectSignal(
    _state => _state.expandedTenantIds,
  )

  /** @see {@link State.expandedAppIds} */
  public readonly expandedAppIdsSg = this.selectSignal(
    _state => _state.expandedAppIds,
  )

  /** Expanded utilities per app */
  public readonly expandedUtilitiesAppIdsSg = this.selectSignal(
    _state => _state.expandedUtilitiesAppIds,
  )

  /** @see {@link State.width} */
  public readonly widthSg = this.selectSignal(_state => _state.width)

  /** @see {@link State.slideoutExpanded} */
  public readonly slideoutExpandedSg = this.selectSignal(
    _state => _state.slideoutExpanded,
  )
  /**
   * This is how much margin should be allocated for the navbar. When the navbar
   * is pinned, this is the same as {@link widthSg}. Otherwise, it is
   * {@link NAVBAR_COLLAPSED_WIDTH}
   */
  public readonly dedicatedWidthSg = ngCore.computed(() =>
    this.pinnedSg() ? this.widthSg() : NAVBAR_COLLAPSED_WIDTH,
  )

  /**
   * Current tenant which the navbar is focused on
   *
   * @see {@link currentTenantIdSg}
   */
  public readonly currentTenantSg =
    this._meFeature.contexts.selectors.tenant.asSignal

  /**
   * ID of the current tenant which the navbar is focused on
   *
   * @see {@link currentTenantSg}
   */
  public readonly currentTenantIdSg =
    this._meFeature.contexts.selectors.tenantId.asSignal

  /**
   * {@link Config} for the {@link currentTenantSg} and {@link currentZoneSg}
   */
  public readonly currentZoneCustomizationSg = ngCore.computed<
    Config | undefined
  >(
    (
      currentTenant = this.currentTenantSg(),
      currentZone = this.currentZoneSg(),
    ) =>
      currentTenant
        ? {
            ...Config.DEFAULT,
            color: currentTenant.color ?? Config.DEFAULT.color,
            id: `zone:${currentTenant.id}-${currentZone?.id ?? 'unknown'}`,
            subtitle: currentZone?.name,
            title: currentTenant.name,
            // TODO: get logo
          }
        : undefined,
  )

  /**
   * Current zone which the navbar is focused on
   *
   * @see {@link currentZoneIdSg}
   */
  public readonly currentZoneSg =
    this._meFeature.contexts.selectors.zone.asSignal

  /**
   * ID of the current zone which the navbar is focused on
   *
   * @see {@link currentZoneSg}
   */
  public readonly currentZoneIdSg =
    this._meFeature.contexts.selectors.zoneId.asSignal

  /**
   * When `true`, the navbar should center around the admin pages, not the
   * {@link currentTenantSg} or {@link currentZoneSg}
   */
  public readonly isOnAdminPageSg = ngCore.computed(
    (currentUrl = this._currentUrlSg()) => !!currentUrl?.startsWith('/admin'),
  )

  /**
   * When `true`, the navbar should center around the GraphQL sandbox, not the
   * {@link currentTenantSg} or {@link currentZoneSg}
   */
  public readonly isOnGraphqlSandboxPageSg = ngCore.computed(
    (currentUrl = this._currentUrlSg()) => !!currentUrl?.startsWith('/graphql'),
  )

  /**
   * When `true`, the navbar is centered around something other than the
   * {@link currentTenantSg} or {@link currentZoneSg}
   *
   * @see {@link isOnAdminPageSg}, {@link isOnGraphqlSandboxPageSg}
   */
  public readonly isNotInTenantSg = ngCore.computed(
    (
      currentTenantId = this.currentTenantIdSg(),
      isOnAdminPage = this.isOnAdminPageSg(),
      isOnGraphqlSandboxPage = this.isOnGraphqlSandboxPageSg(),
    ) =>
      // If there is not a current tenant or we are on a non-tenant page, then
      // the navbar is not currently focused on a tenant
      !currentTenantId || isOnAdminPage || isOnGraphqlSandboxPage,
  )

  /**
   * Indicates whether or not the current user is allowed to access the admin
   * pages
   */
  public readonly canAccessAdminPagesSg = ngCore.computed(
    (
      currentUser = this._meFeature.selectors.currentUser.asSignal(),
      tenants = this._meFeature.contexts.selectors.availableTenants.asSignal(),
    ) =>
      Boolean(currentUser?.isPlatformAdmin) ||
      tenants.some(tenant =>
        ents.core.admin.Access.grantsTenantAdminPortalAccess(tenant.access),
      ),
  )

  /**
   * Indicates whether or not the current user is allowed to access the GraphQL
   * sandbox
   */
  public readonly canAccessGraphqlSandboxSg = ngCore.computed(
    () => !environment.production,
  )

  /** @see {@link State.config} */
  public readonly configSg = this.selectSignal(_state => _state.config)

  /** @see {@link State.configHasRecentlyChanged} */
  public readonly configHasRecentlyChangedSg = this.selectSignal(
    _state => _state.configHasRecentlyChanged,
  )

  /** @see {@link Config.id} */
  public readonly configIdSg = ngCore.computed(
    (config = this.configSg()) => config.id,
  )

  /** @see {@link Config.color} */
  public readonly colorSg = ngCore.computed(
    (config = this.configSg()) => config.color,
  )

  /** @see {@link Config.logo} */
  public readonly logoSg = ngCore.computed(
    (config = this.configSg()) => config.logo,
  )

  /** @see {@link State.hovered} */
  public readonly hoveredSg = this.selectSignal(_state => _state.hovered)

  /** @see {@link State.pinned} */
  public readonly pinnedSg = this.selectSignal(_state => _state.pinned)

  /** @see {@link State.logoHovered} */
  public readonly logoHoveredSg = this.selectSignal(
    _state => _state.logoHovered,
  )

  /** @see {@link State.logoMenuOpen} */
  public readonly logoMenuOpenSg = this.selectSignal(
    _state => _state.logoMenuOpen,
  )

  /**
   * Indicates the navbar is in the expanded/wider state
   */
  public readonly expandedSg = ngCore.computed(
    (
      hovered = this.hoveredSg(),
      pinned = this.pinnedSg(),
      logoMenuOpen = this.logoMenuOpenSg(),
    ) => hovered || pinned || logoMenuOpen,
  )

  /**
   * Used to expand the navbar on hover after a short delay, to avoid cases of
   * expanding when someone accidentlaly hovers over the navbar for a fraction
   * of a second
   */
  private _hoverExpansionTimeout: unknown

  constructor() {
    super(State.INITIAL)

    // Restores `pinned` from local storage
    const pinnedLocalStorageValue = window.localStorage.getItem(
      PINNED_LOCAL_STORAGE_KEY,
    )
    if (pinnedLocalStorageValue !== null) {
      this.patchState({ pinned: pinnedLocalStorageValue === 'true' })
    }
    // Saves `pinned` to local storage
    ngCore.effect(() => {
      const pinned = this.pinnedSg()
      window.localStorage.setItem(PINNED_LOCAL_STORAGE_KEY, pinned.toString())
    })

    for (const { localStorageKey, stateKey } of [
      {
        localStorageKey: EXPANDED_TENANT_IDS_LOCAL_STORAGE_KEY,
        stateKey: 'expandedTenantIds',
      },
      {
        localStorageKey: EXPANDED_APP_IDS_LOCAL_STORAGE_KEY,
        stateKey: 'expandedAppIds',
      },
    ] satisfies readonly {
      localStorageKey: string
      stateKey: Extract<keyof State, `expanded${string}Ids`>
    }[]) {
      // Restores `expandedIds` from local storage
      const expandedIdsLocalStorageValue =
        window.localStorage.getItem(localStorageKey)
      if (expandedIdsLocalStorageValue !== null) {
        const expandedIdsLocalStorageParsedValue: unknown = JSON.parse(
          expandedIdsLocalStorageValue,
        )
        if (Array.isArray(expandedIdsLocalStorageParsedValue)) {
          this.patchState({
            [stateKey]: new Set(
              expandedIdsLocalStorageParsedValue.filter(
                (id: unknown): id is string => TypeChecker.isString(id),
              ),
            ),
          })
        }
      }
      // Saves `expandedIds` to local storage
      ngCore.effect(() => {
        const expandedIds = this[`${stateKey}Sg`]()
        window.localStorage.setItem(
          localStorageKey,
          JSON.stringify(Array.from(expandedIds)),
        )
      })
    }

    /**
     * Sets the {@link State.config} based on where the user is in the
     * platform
     */
    ngCore.effect(() => {
      this.patchState({
        config: this.isOnAdminPageSg()
          ? Config.ADMIN
          : this.isOnGraphqlSandboxPageSg()
            ? Config.GRAPHQL
            : (this.currentZoneCustomizationSg() ?? Config.DEFAULT),
      })
    })

    let lastConfigId: string | undefined
    /**
     * Detects changes in {@link configIdSg} and records them to {@link State.configHasRecentlyChanged}
     */
    ngCore.effect(() => {
      const configId = this.configIdSg()

      if (configId !== lastConfigId) {
        if (lastConfigId && lastConfigId !== Config.DEFAULT.id) {
          this.patchState({ configHasRecentlyChanged: true })
          setTimeout(() => {
            this.patchState({ configHasRecentlyChanged: false })
          }, CONFIG_TRANSITION_TIME)
        }
        lastConfigId = configId
      }
    })
  }

  /**
   * User has started hovering over the navbar
   */
  public hoverStart(): void {
    // NOTE:
    this._hoverExpansionTimeout = setTimeout(() => {
      this.patchState({ hovered: true })
    }, NAVBAR_HOVER_EXPAND_DELAY)
  }

  /**
   * User has stopped hovering over the navbar
   */
  public hoverEnd(): void {
    if (this._hoverExpansionTimeout) {
      // NOTE: node and browsers have different types for this. We can't reference
      // the node types because that would break when we build the frontend, but
      // the node types seem to be applied in VS Code, so we just type it as
      // `unknown` and cast it here
      clearTimeout(this._hoverExpansionTimeout as number)
      delete this._hoverExpansionTimeout
    }
    this.patchState({ hovered: false })
  }

  /**
   * Toggles whether or not the navbar is pinned
   */
  public togglePin(): void {
    this.patchState(({ hovered, pinned }) => ({
      hovered: hovered && !pinned,
      pinned: !pinned,
    }))
  }

  /** Toggles whether or not the assistant slideout is expanded */
  public toggleExpandCollapseSlideout(): void {
    this.patchState(({ slideoutExpanded }) => ({
      slideoutExpanded: !slideoutExpanded,
    }))
  }

  /**
   * User has started hovering over the logo
   */
  public logoHoverStart(): void {
    this.patchState({ logoHovered: true })
  }

  /**
   * User has stopped hovering over the logo
   */
  public logoHoverEnd(): void {
    this.patchState({ logoHovered: false })
  }

  /**
   * Toggles whether or not the logo menu is open
   */
  public toggleLogoMenuOpen(): void {
    const isOnGraphqlSandboxPage = this.isOnGraphqlSandboxPageSg()
    const isOnAdminPage = this.isOnAdminPageSg()
    const isLogoMenuCurrentlyOpen = this.logoMenuOpenSg()

    // On GraphQL sandbox or admin pages, refresh user data when opening the
    // logo menu to display newly created resources
    if ((isOnGraphqlSandboxPage || isOnAdminPage) && !isLogoMenuCurrentlyOpen) {
      this._meFeature.actions.get.from('Navbar.LogoMenu.Clicked').dispatch()
    }

    this.patchState(({ logoMenuOpen }) => ({ logoMenuOpen: !logoMenuOpen }))
  }

  /**
   * Toggles whether or not the given tenant is expanded in the logo menu
   */
  public toggleTenantExpanded(tenantId: ents.core.admin.Tenant.Id): void {
    this.patchState(({ expandedTenantIds }) => {
      const newExpandedTenantIds = new Set(expandedTenantIds)
      if (expandedTenantIds.has(tenantId)) {
        newExpandedTenantIds.delete(tenantId)
      } else {
        newExpandedTenantIds.add(tenantId)
      }
      return { expandedTenantIds: newExpandedTenantIds }
    })
  }

  /** Toggles whether or not the given app is expanded in the app menu */
  public toggleAppExpanded(appId: ents.core.admin.App.Id): void {
    this.patchState(({ expandedAppIds }) => {
      const newExpandedAppIds = new Set(expandedAppIds)
      if (expandedAppIds.has(appId)) {
        newExpandedAppIds.delete(appId)
      } else {
        newExpandedAppIds.add(appId)
      }
      return { expandedAppIds: newExpandedAppIds }
    })
  }

  /** Toggles whether or not the utilities of a given app are expanded */
  public toggleUtilitiesExpanded(appId: ents.core.admin.App.Id): void {
    this.patchState(({ expandedUtilitiesAppIds }) => {
      const newExpandedUtilitiesAppIds = new Set(expandedUtilitiesAppIds)
      if (expandedUtilitiesAppIds.has(appId)) {
        newExpandedUtilitiesAppIds.delete(appId)
      } else {
        newExpandedUtilitiesAppIds.add(appId)
      }
      return { expandedUtilitiesAppIds: newExpandedUtilitiesAppIds }
    })
  }

  /** Updates navbar width */
  public updateWidth(width: number): void {
    this.patchState({ width })
  }
}
